import React from "react";
import useReveal from "@/hooks/useReveal";
import { useI18n } from "@/context/LangContext";
import { MapPin, ArrowRight } from "@phosphor-icons/react";

const STORIES = [
  { flag: "🇳🇬", country: "Nigeria", city: "Lagos", key: "ng", indKey: "logistics" },
  { flag: "🇧🇼", country: "Botswana", city: "Gaborone", key: "bw", indKey: "retail" },
  { flag: "🇿🇲", country: "Zambia", city: "Lusaka", key: "zm", indKey: "manufacturing" },
  { flag: "🇿🇼", country: "Zimbabwe", city: "Harare", key: "zw", indKey: "banking" },
  { flag: "🇦🇪", country: "UAE", city: "Dubai", key: "ae", indKey: "healthcare" },
  { flag: "🇮🇳", country: "India", city: "Bengaluru", key: "in", indKey: "education" },
];

export default function SuccessStories() {
  const ref = useReveal();
  const { t } = useI18n();
  return (
    <section
      id="success"
      ref={ref}
      data-testid="success-stories"
      className="reveal section border-b border-[color:var(--line)] bg-white"
    >
      <div className="container-x">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10 md:mb-12">
          <div>
            <div className="flex items-center gap-3">
              <span className="w-6 h-px bg-[color:var(--brand)]" />
              <div className="eyebrow">{t("success.eyebrow")}</div>
            </div>
            <h2 className="font-display font-black text-4xl md:text-5xl mt-4 leading-[1.02] tracking-tight max-w-3xl">
              {t("success.title")}
            </h2>
          </div>
          <p className="max-w-md text-neutral-600 text-sm md:text-base">
            {t("success.sub")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {STORIES.map((s) => (
            <article
              key={s.country}
              data-testid={`success-${s.country.toLowerCase()}`}
              className="border border-[color:var(--line)] bg-white hover:border-[color:var(--brand)] transition-colors p-6 flex flex-col group"
            >
              <div className="flex items-center gap-3">
                <span className="text-3xl leading-none" aria-hidden>{s.flag}</span>
                <div>
                  <div className="font-display font-bold text-base leading-tight">{s.country}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1 mt-0.5">
                    <MapPin size={10} weight="bold" /> {s.city} · {t(`success.ind.${s.indKey}`)}
                  </div>
                </div>
              </div>

              <div className="mt-5 flex-1 space-y-4 text-sm">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">{t("success.problem")}</div>
                  <p className="mt-1 text-neutral-700 leading-relaxed">{t(`success.${s.key}.p`)}</p>
                </div>
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">{t("success.solution")}</div>
                  <p className="mt-1 text-neutral-700 leading-relaxed">{t(`success.${s.key}.s`)}</p>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-[color:var(--line)] flex items-center justify-between">
                <div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">{t("success.result")}</div>
                  <div className="mt-1 font-display font-black text-lg text-[color:var(--brand)] leading-tight">{t(`success.${s.key}.r`)}</div>
                </div>
                <ArrowRight size={18} weight="bold" className="text-neutral-300 group-hover:text-[color:var(--brand)] group-hover:translate-x-1 transition-all" />
              </div>
            </article>
          ))}
        </div>

        <p className="mt-6 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          {t("success.disclaimer")}
        </p>
      </div>
    </section>
  );
}
